import { useCallback, useEffect, useRef, useState } from "react";
import type { User } from "firebase/auth";

import { callAuthenticatedWordinariusFunction } from "../platform/firebaseFunctions";

export interface MissionRuntimeMetrics {
  attempts: number;
  exactPasses: number;
  fluencyScoreSum: number;
  stars: number;
  xp: number;
  currentStreak: number;
  bestStreak: number;
}

export interface MissionNavigationState {
  currentStepIndex: number;
  unlockedStepIndex: number;
}

export interface WebProgressState {
  scenarioProgressById: Record<string, number>;
  passedPracticeItemIdsByScenario: Record<string, string[]>;
  failedPracticeItemIdsByScenario: Record<string, string[]>;
  reviewedVocabularyItemIdsByScenario: Record<string, string[]>;
  completedVocabularyExerciseKeysByScenario: Record<string, string[]>;
  vocabularyExerciseScoresByScenario: Record<string, Record<string, number>>;
  completedScenarioIds: string[];
  missionMetricsByScenario: Record<string, MissionRuntimeMetrics>;
  missionNavigationByScenario: Record<string, MissionNavigationState>;
  rewardedMissionStepIdsByScenario: Record<string, string[]>;
  failedMissionStepIdsByScenario: Record<string, string[]>;
}

interface ProgressScope {
  targetLanguage: string;
  level: string;
}

interface ProgressSnapshotResponse {
  exists: boolean;
  state: WebProgressState | null;
}

const STORAGE_PREFIX = "wordinarius_echo.session_progress.v1";

export function emptyWebProgress(): WebProgressState {
  return {
    scenarioProgressById: {},
    passedPracticeItemIdsByScenario: {},
    failedPracticeItemIdsByScenario: {},
    reviewedVocabularyItemIdsByScenario: {},
    completedVocabularyExerciseKeysByScenario: {},
    vocabularyExerciseScoresByScenario: {},
    completedScenarioIds: [],
    missionMetricsByScenario: {},
    missionNavigationByScenario: {},
    rewardedMissionStepIdsByScenario: {},
    failedMissionStepIdsByScenario: {},
  };
}

function number(value: unknown, maximum = Number.MAX_SAFE_INTEGER): number {
  const parsed = Math.round(Number(value) || 0);
  return Number.isFinite(parsed) ? Math.min(maximum, Math.max(0, parsed)) : 0;
}

function strings(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return Array.from(new Set(value.map((item) => String(item || "").trim()).filter(Boolean)));
}

function stringArrayMap(value: unknown): Record<string, string[]> {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  return Object.fromEntries(Object.entries(value).map(([key, items]) => [key, strings(items)]));
}

function numberMap(value: unknown, maximum: number): Record<string, number> {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, number(item, maximum)]));
}

function scoreMap(value: unknown): Record<string, Record<string, number>> {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  return Object.fromEntries(Object.entries(value).map(([scenarioId, scores]) => [scenarioId, numberMap(scores, 10)]));
}

function missionMetricsMap(value: unknown): Record<string, MissionRuntimeMetrics> {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  return Object.fromEntries(Object.entries(value).map(([scenarioId, raw]) => {
    const entry = raw && typeof raw === "object" ? raw as Record<string, unknown> : {};
    return [scenarioId, {
      attempts: number(entry.attempts),
      exactPasses: number(entry.exactPasses),
      fluencyScoreSum: number(entry.fluencyScoreSum),
      stars: number(entry.stars),
      xp: number(entry.xp),
      currentStreak: number(entry.currentStreak),
      bestStreak: number(entry.bestStreak),
    }];
  }));
}

function missionNavigationMap(value: unknown): Record<string, MissionNavigationState> {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  return Object.fromEntries(Object.entries(value).map(([scenarioId, raw]) => {
    const entry = raw && typeof raw === "object" ? raw as Record<string, unknown> : {};
    const currentStepIndex = number(entry.currentStepIndex);
    return [scenarioId, {
      currentStepIndex,
      unlockedStepIndex: Math.max(currentStepIndex, number(entry.unlockedStepIndex)),
    }];
  }));
}

export function normalizeWebProgress(value: unknown): WebProgressState {
  if (!value || typeof value !== "object" || Array.isArray(value)) return emptyWebProgress();
  const raw = value as Record<string, unknown>;
  return {
    scenarioProgressById: numberMap(raw.scenarioProgressById, 100),
    passedPracticeItemIdsByScenario: stringArrayMap(raw.passedPracticeItemIdsByScenario),
    failedPracticeItemIdsByScenario: stringArrayMap(raw.failedPracticeItemIdsByScenario),
    reviewedVocabularyItemIdsByScenario: stringArrayMap(raw.reviewedVocabularyItemIdsByScenario),
    completedVocabularyExerciseKeysByScenario: stringArrayMap(raw.completedVocabularyExerciseKeysByScenario),
    vocabularyExerciseScoresByScenario: scoreMap(raw.vocabularyExerciseScoresByScenario),
    completedScenarioIds: strings(raw.completedScenarioIds),
    missionMetricsByScenario: missionMetricsMap(raw.missionMetricsByScenario),
    missionNavigationByScenario: missionNavigationMap(raw.missionNavigationByScenario),
    rewardedMissionStepIdsByScenario: stringArrayMap(raw.rewardedMissionStepIdsByScenario),
    failedMissionStepIdsByScenario: stringArrayMap(raw.failedMissionStepIdsByScenario),
  };
}

function mergeArrays(left: string[] = [], right: string[] = []): string[] {
  return strings([...left, ...right]);
}

function mergeArrayMaps(left: Record<string, string[]>, right: Record<string, string[]>): Record<string, string[]> {
  return Object.fromEntries(Array.from(new Set([...Object.keys(left), ...Object.keys(right)]))
    .map((key) => [key, mergeArrays(left[key], right[key])]));
}

function mergeNumberMaps(left: Record<string, number>, right: Record<string, number>): Record<string, number> {
  return Object.fromEntries(Array.from(new Set([...Object.keys(left), ...Object.keys(right)]))
    .map((key) => [key, Math.max(left[key] || 0, right[key] || 0)]));
}

export function mergeWebProgress(first: WebProgressState, second: WebProgressState): WebProgressState {
  const left = normalizeWebProgress(first);
  const right = normalizeWebProgress(second);
  const scenarioIds = Array.from(new Set([
    ...Object.keys(left.vocabularyExerciseScoresByScenario),
    ...Object.keys(right.vocabularyExerciseScoresByScenario),
  ]));
  const metricIds = Array.from(new Set([
    ...Object.keys(left.missionMetricsByScenario),
    ...Object.keys(right.missionMetricsByScenario),
  ]));
  const navigationIds = Array.from(new Set([
    ...Object.keys(left.missionNavigationByScenario),
    ...Object.keys(right.missionNavigationByScenario),
  ]));
  return {
    scenarioProgressById: mergeNumberMaps(left.scenarioProgressById, right.scenarioProgressById),
    passedPracticeItemIdsByScenario: mergeArrayMaps(left.passedPracticeItemIdsByScenario, right.passedPracticeItemIdsByScenario),
    failedPracticeItemIdsByScenario: mergeArrayMaps(left.failedPracticeItemIdsByScenario, right.failedPracticeItemIdsByScenario),
    reviewedVocabularyItemIdsByScenario: mergeArrayMaps(left.reviewedVocabularyItemIdsByScenario, right.reviewedVocabularyItemIdsByScenario),
    completedVocabularyExerciseKeysByScenario: mergeArrayMaps(left.completedVocabularyExerciseKeysByScenario, right.completedVocabularyExerciseKeysByScenario),
    vocabularyExerciseScoresByScenario: Object.fromEntries(scenarioIds.map((scenarioId) => [
      scenarioId,
      mergeNumberMaps(left.vocabularyExerciseScoresByScenario[scenarioId] || {}, right.vocabularyExerciseScoresByScenario[scenarioId] || {}),
    ])),
    completedScenarioIds: mergeArrays(left.completedScenarioIds, right.completedScenarioIds),
    missionMetricsByScenario: Object.fromEntries(metricIds.map((scenarioId) => {
      const a = left.missionMetricsByScenario[scenarioId];
      const b = right.missionMetricsByScenario[scenarioId];
      return [scenarioId, {
        attempts: Math.max(a?.attempts || 0, b?.attempts || 0),
        exactPasses: Math.max(a?.exactPasses || 0, b?.exactPasses || 0),
        fluencyScoreSum: Math.max(a?.fluencyScoreSum || 0, b?.fluencyScoreSum || 0),
        stars: Math.max(a?.stars || 0, b?.stars || 0),
        xp: Math.max(a?.xp || 0, b?.xp || 0),
        currentStreak: Math.max(a?.currentStreak || 0, b?.currentStreak || 0),
        bestStreak: Math.max(a?.bestStreak || 0, b?.bestStreak || 0),
      }];
    })),
    missionNavigationByScenario: Object.fromEntries(navigationIds.map((scenarioId) => {
      const currentStepIndex = Math.max(
        left.missionNavigationByScenario[scenarioId]?.currentStepIndex || 0,
        right.missionNavigationByScenario[scenarioId]?.currentStepIndex || 0,
      );
      return [scenarioId, {
        currentStepIndex,
        unlockedStepIndex: Math.max(
          currentStepIndex,
          left.missionNavigationByScenario[scenarioId]?.unlockedStepIndex || 0,
          right.missionNavigationByScenario[scenarioId]?.unlockedStepIndex || 0,
        ),
      }];
    })),
    rewardedMissionStepIdsByScenario: mergeArrayMaps(left.rewardedMissionStepIdsByScenario, right.rewardedMissionStepIdsByScenario),
    failedMissionStepIdsByScenario: mergeArrayMaps(left.failedMissionStepIdsByScenario, right.failedMissionStepIdsByScenario),
  };
}

function storageKey(scope: ProgressScope): string {
  return `${STORAGE_PREFIX}.${scope.targetLanguage.trim().toLowerCase()}.${scope.level.trim().toLowerCase()}`;
}

export function loadLocalWebProgress(scope: ProgressScope): WebProgressState {
  if (typeof window === "undefined") return emptyWebProgress();
  try {
    return normalizeWebProgress(JSON.parse(window.localStorage.getItem(storageKey(scope)) || "null"));
  } catch {
    return emptyWebProgress();
  }
}

export function saveLocalWebProgress(scope: ProgressScope, state: WebProgressState): void {
  try {
    window.localStorage.setItem(storageKey(scope), JSON.stringify({
      ...normalizeWebProgress(state),
      updatedAt: new Date().toISOString(),
    }));
  } catch {
    // Progress remains in memory and can still sync remotely when local storage is blocked.
  }
}

function append(map: Record<string, string[]>, scenarioId: string, value: string): Record<string, string[]> {
  return { ...map, [scenarioId]: mergeArrays(map[scenarioId], [value]) };
}

export function recordPracticeResult(
  state: WebProgressState,
  scenarioId: string,
  sentenceId: string,
  passed: boolean,
  totalItems: number,
): WebProgressState {
  const passedMap = passed ? append(state.passedPracticeItemIdsByScenario, scenarioId, sentenceId) : state.passedPracticeItemIdsByScenario;
  const failedMap = passed ? state.failedPracticeItemIdsByScenario : append(state.failedPracticeItemIdsByScenario, scenarioId, sentenceId);
  const completed = passedMap[scenarioId]?.length || 0;
  return normalizeWebProgress({
    ...state,
    passedPracticeItemIdsByScenario: passedMap,
    failedPracticeItemIdsByScenario: failedMap,
    scenarioProgressById: {
      ...state.scenarioProgressById,
      [scenarioId]: Math.round((completed / Math.max(1, totalItems)) * 100),
    },
  });
}

export function recordVocabularyResult(
  state: WebProgressState,
  scenarioId: string,
  vocabularyId: string,
  mode: string,
  score: number,
): WebProgressState {
  const completionKey = `${mode}::${vocabularyId}`;
  return normalizeWebProgress({
    ...state,
    reviewedVocabularyItemIdsByScenario: append(state.reviewedVocabularyItemIdsByScenario, scenarioId, vocabularyId),
    completedVocabularyExerciseKeysByScenario: append(state.completedVocabularyExerciseKeysByScenario, scenarioId, completionKey),
    vocabularyExerciseScoresByScenario: {
      ...state.vocabularyExerciseScoresByScenario,
      [scenarioId]: {
        ...(state.vocabularyExerciseScoresByScenario[scenarioId] || {}),
        [completionKey]: Math.max(score, state.vocabularyExerciseScoresByScenario[scenarioId]?.[completionKey] || 0),
      },
    },
  });
}

export function recordMissionResult(
  state: WebProgressState,
  scenarioId: string,
  objectiveId: string,
  passed: boolean,
  stepIndex: number,
): WebProgressState {
  const previous = state.missionMetricsByScenario[scenarioId] || {
    attempts: 0, exactPasses: 0, fluencyScoreSum: 0, stars: 0, xp: 0, currentStreak: 0, bestStreak: 0,
  };
  const alreadyRewarded = state.rewardedMissionStepIdsByScenario[scenarioId]?.includes(objectiveId) === true;
  const rewardXp = passed && !alreadyRewarded ? 15 : 0;
  const rewardStars = passed && !alreadyRewarded ? 3 : 0;
  const currentStreak = passed ? previous.currentStreak + 1 : 0;
  return normalizeWebProgress({
    ...state,
    missionMetricsByScenario: {
      ...state.missionMetricsByScenario,
      [scenarioId]: {
        attempts: previous.attempts + 1,
        exactPasses: previous.exactPasses + (passed ? 1 : 0),
        fluencyScoreSum: previous.fluencyScoreSum + (passed ? 100 : 0),
        stars: previous.stars + rewardStars,
        xp: previous.xp + rewardXp,
        currentStreak,
        bestStreak: Math.max(previous.bestStreak, currentStreak),
      },
    },
    missionNavigationByScenario: {
      ...state.missionNavigationByScenario,
      [scenarioId]: { currentStepIndex: stepIndex, unlockedStepIndex: passed ? stepIndex + 1 : stepIndex },
    },
    rewardedMissionStepIdsByScenario: passed
      ? append(state.rewardedMissionStepIdsByScenario, scenarioId, objectiveId)
      : state.rewardedMissionStepIdsByScenario,
    failedMissionStepIdsByScenario: passed
      ? state.failedMissionStepIdsByScenario
      : append(state.failedMissionStepIdsByScenario, scenarioId, objectiveId),
  });
}

export function useWebProgress(scope: ProgressScope | null, user: User | null) {
  const [state, setState] = useState<WebProgressState>(emptyWebProgress);
  const [ready, setReady] = useState(false);
  const [syncError, setSyncError] = useState(false);
  const revision = useRef(0);

  useEffect(() => {
    let active = true;
    setReady(false);
    setSyncError(false);
    if (!scope) {
      setState(emptyWebProgress());
      setReady(true);
      return () => { active = false; };
    }
    const local = loadLocalWebProgress(scope);
    setState(local);
    if (!user) {
      setReady(true);
      return () => { active = false; };
    }
    void callAuthenticatedWordinariusFunction<ProgressSnapshotResponse>("getSessionProgressSnapshot", scope)
      .then(async (response) => {
        const merged = response.state ? mergeWebProgress(local, response.state) : local;
        if (!active) return;
        saveLocalWebProgress(scope, merged);
        setState(merged);
        await callAuthenticatedWordinariusFunction("saveSessionProgressSnapshot", { ...scope, state: merged });
      })
      .catch(() => active && setSyncError(true))
      .finally(() => active && setReady(true));
    return () => { active = false; };
  }, [scope?.level, scope?.targetLanguage, user?.uid]);

  useEffect(() => {
    if (!scope) return;
    const key = storageKey(scope);
    const onStorage = (event: StorageEvent) => {
      if (event.key !== key || !event.newValue) return;
      try {
        const incoming = normalizeWebProgress(JSON.parse(event.newValue));
        setState((current) => mergeWebProgress(current, incoming));
      } catch {
        // Ignore malformed writes from another tab and keep the valid snapshot.
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [scope?.level, scope?.targetLanguage]);

  const update = useCallback((mutator: (current: WebProgressState) => WebProgressState) => {
    if (!scope) return;
    const currentRevision = ++revision.current;
    setState((current) => {
      const next = normalizeWebProgress(mutator(current));
      saveLocalWebProgress(scope, next);
      if (user) {
        window.setTimeout(() => {
          if (revision.current !== currentRevision) return;
          void callAuthenticatedWordinariusFunction("saveSessionProgressSnapshot", { ...scope, state: next })
            .catch(() => setSyncError(true));
        }, 350);
      }
      return next;
    });
  }, [scope?.level, scope?.targetLanguage, user?.uid]);

  return { ready, state, syncError, update };
}
